import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_ISAAC_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

/**
 * Extract server number from either direct input (e.g., "ct729") or URL (e.g., "https://ct729.isaachosting.ca/isaacapi")
 */
function extractServerNumber(input: string): string {
  const trimmedInput = input.trim()

  // Check if it's a URL format
  if (trimmedInput.startsWith('http')) {
    try {
      const url = new URL(trimmedInput)
      const hostname = url.hostname

      // Extract server number from hostname like "ct729.isaachosting.ca"
      const serverMatch = hostname.match(/^(ct\d{3})\./)
      if (serverMatch) {
        return serverMatch[1]
      }
    } catch {
      // Invalid URL, will be handled by validation
    }
  }

  // Check if it's already in the correct format (ct + 3 digits)
  if (/^ct\d{3}$/i.test(trimmedInput)) {
    return trimmedInput.toLowerCase()
  }

  // Return the original input for validation to handle
  return trimmedInput
}

type EnableISAACAppInput = {
  apiKey: string
  appType: AppTypeEnum
  password: string
  portalServerNumber: string
  username: string
}

const formSchema = z.object({
  apiKey: z.string().min(1, 'Authorization code is required'),
  password: z.string().min(1, 'Password is required'),
  portalServerNumber: z
    .string()
    .min(1, 'Portal server number or URL is required')
    .transform((val) => extractServerNumber(val))
    .refine((val) => /^ct\d{3}$/.test(val), {
      message:
        'Invalid format. Please provide either a server number (ct123) or full URL (https://ct123.isaachosting.ca/isaacapi)'
    }),
  username: z.string().min(1, 'Username is required')
})

/**
 * This is the modal for the ISAAC app.
 */
export function ISAACModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      apiKey: '',
      password: '',
      portalServerNumber: '',
      username: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableISAACApp, { loading }] = useMutation<
    { enableISAACApp: { apiKey: string; password: string; portalServerNumber: string; username: string } },
    EnableISAACAppInput
  >(ENABLE_ISAAC_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('ISAAC app enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'Your username or password is invalid.'
          ? 'Invalid username/password. Please try again.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`

      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableISAACApp({
      variables: {
        apiKey: values.apiKey,
        appType: appType as AppTypeEnum,
        password: values.password,
        portalServerNumber: values.portalServerNumber,
        username: values.username
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable ISAAC for Chain</DialogTitle>
          <DialogDescription>
            Enabling this integration requires an admin ISAAC account. Enter your username and password.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your ISAAC API username" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter your ISAAC API password" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Authorization Code</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter the Authorization Code" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="portalServerNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ISAAC Portal Server Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter server number (ct729) or full URL (https://ct729.isaachosting.ca/isaacapi)"
                      {...field}
                      disabled={loading}
                      className="font-mono"
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground mt-1">
                    You can provide either the server number (e.g.,{' '}
                    <span className="font-mono font-semibold">ct729</span>) or paste the full URL from your ISAAC portal
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href={app.docs}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                Read the integration guide
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
